import { createEloItemInstance } from "./C1_ensureEloEquipmentDefaults";
import type { C1_EquipmentStandard } from "../types/C1_ModelTypes";
import type { C1_EloItem_Data, C1_EloItem_Definition } from "../types/C1_EloTypes";

export function findEloItemData(root: C1_EloItem_Data, itemId: string): C1_EloItem_Data | undefined {
  if (root.id === itemId) return root;
  for (const child of root.items) {
    const found = findEloItemData(child, itemId);
    if (found) return found;
  }
  return undefined;
}

export function updateEloItemData(
  root: C1_EloItem_Data,
  itemId: string,
  update: (item: C1_EloItem_Data) => C1_EloItem_Data,
): C1_EloItem_Data {
  if (root.id === itemId) return update(root);
  let changed = false;
  const items = root.items.map((child) => {
    const next = updateEloItemData(child, itemId, update);
    if (next !== child) changed = true;
    return next;
  });
  return changed ? { ...root, items } : root;
}

export function repeatItemCount(definition: C1_EloItem_Definition, instance: C1_EloItem_Data): number {
  return Math.max(0, Math.floor(instance.details?.itemCount ?? definition.details?.defaultItemCount ?? 0));
}

export function repeatItemLimit(definition: C1_EloItem_Definition): number {
  const repeatCount = definition.details?.repeatCount;
  return Array.isArray(repeatCount) ? Math.max(0, ...repeatCount) : typeof repeatCount === "number" ? repeatCount : 0;
}

function repeatedDefinitionId(baseId: string, repeatIndex: number): string {
  return `${baseId}-${repeatIndex}`;
}

export function nextRepeatedItemDefinitions(
  definition: C1_EloItem_Definition,
  instance: C1_EloItem_Data,
): C1_EloItem_Definition[] {
  const nextIndex = repeatItemCount(definition, instance) + 1;
  if (nextIndex > repeatItemLimit(definition)) return [];
  return (definition.items ?? []).map((baseDefinition) => ({
    ...baseDefinition,
    id: repeatedDefinitionId(baseDefinition.id, nextIndex),
    repeatIndex: nextIndex,
    defaultDetails: {
      ...baseDefinition.defaultDetails,
      ...definition.repeatDefaults?.[nextIndex - 1]?.details,
    },
    description: {
      de: `${baseDefinition.repeatDescription?.de ?? baseDefinition.description.de} #${nextIndex}`,
      en: `${baseDefinition.repeatDescription?.en ?? baseDefinition.description.en} #${nextIndex}`,
    },
  }));
}

function repeatedChildIndex(definition: C1_EloItem_Definition, child: C1_EloItem_Data): number | undefined {
  for (const baseDefinition of definition.items ?? []) {
    if (!child.definitionId.startsWith(`${baseDefinition.id}-`)) continue;
    const index = Number(child.definitionId.slice(baseDefinition.id.length + 1));
    if (Number.isInteger(index) && index > 0) return index;
  }
  return undefined;
}

export function removeRepeatedItem(
  root: C1_EloItem_Data,
  groupItemId: string,
  childItemId: string,
  definition: C1_EloItem_Definition,
): C1_EloItem_Data {
  return updateEloItemData(root, groupItemId, (group) => {
    const selectedChild = group.items.find((item) => item.id === childItemId);
    const removedIndex = selectedChild ? repeatedChildIndex(definition, selectedChild) : undefined;
    if (removedIndex === undefined) return group;

    const baseDefinitions = definition.items ?? [];
    const items = group.items.flatMap((item) => {
      const itemIndex = repeatedChildIndex(definition, item);
      if (itemIndex === undefined) return [item];
      if (itemIndex === removedIndex) return [];
      if (itemIndex < removedIndex) return [item];
      const baseDefinition = baseDefinitions.find((candidate) => item.definitionId.startsWith(`${candidate.id}-`));
      if (!baseDefinition) return [item];
      const defaultNames = [
        `${baseDefinition.repeatDescription?.de ?? baseDefinition.description.de} #${itemIndex}`,
        `${baseDefinition.repeatDescription?.en ?? baseDefinition.description.en} #${itemIndex}`,
      ];
      return [{
        ...item,
        definitionId: repeatedDefinitionId(baseDefinition.id, itemIndex - 1),
        name: item.name && !defaultNames.includes(item.name)
          ? item.name
          : `${baseDefinition.repeatDescription?.de ?? baseDefinition.description.de} #${itemIndex - 1}`,
      }];
    });

    return {
      ...group,
      details: { ...group.details, itemCount: Math.max(0, repeatItemCount(definition, group) - 1) },
      items,
    };
  });
}

export function appendRepeatedItem(
  root: C1_EloItem_Data,
  groupItemId: string,
  definition: C1_EloItem_Definition,
  equipmentStandard: C1_EquipmentStandard,
): C1_EloItem_Data {
  return updateEloItemData(root, groupItemId, (group) => {
    const nextIndex = repeatItemCount(definition, group) + 1;
    const items = nextRepeatedItemDefinitions(definition, group).map((itemDefinition) =>
      createEloItemInstance(itemDefinition, equipmentStandard),
    );
    if (!items.length) return group;
    return {
      ...group,
      details: { ...group.details, itemCount: nextIndex },
      items: [...group.items, ...items],
    };
  });
}

export function updateEloItemDetails(
  root: C1_EloItem_Data,
  itemId: string,
  details: Partial<NonNullable<C1_EloItem_Data["details"]>>,
): C1_EloItem_Data {
  return updateEloItemData(root, itemId, (item) => ({
    ...item,
    details: { ...item.details, ...details },
  }));
}

export function appendEloItem(
  root: C1_EloItem_Data,
  parentItemId: string,
  definition: C1_EloItem_Definition,
  equipmentStandard: C1_EquipmentStandard,
): C1_EloItem_Data {
  return updateEloItemData(root, parentItemId, (parent) =>
    parent.items.some((item) => item.definitionId === definition.id)
      ? parent
      : { ...parent, items: [...parent.items, createEloItemInstance(definition, equipmentStandard)] },
  );
}
